import classnames from "classnames";
import React, { ButtonHTMLAttributes, Ref, SyntheticEvent } from "react";

import "./button.scss";

export type ButtonComponentProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    children?: React.ReactNode;
    className?: string;
    form?: string;
    block?: boolean;
    stopPropagation?: boolean;
    onClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
    size?: "md" | "sm" | "lg";
    color?:
        | "primary"
        | "secondary"
        | "gray"
        | "link"
        | "transparent"
        | "danger"
        | "light-gray"
        | "dark"
        | "warning";
    outline?: boolean;
    fullWidth?: boolean;
    fullWidthOnMobile?: boolean;
    disabled?: boolean;
    loading?: boolean;
    type?: "submit" | "button";
    test?: string;
    tabIndex?: number;
    dataAttributes?: Record<string, string | undefined>;
    ref?: Ref<HTMLButtonElement>;
};

const Button = ({
    ref: buttonRef,
    children,
    className = "",
    form,
    stopPropagation = true,
    onClick,
    size = "md",
    color = "primary",
    outline,
    fullWidth,
    fullWidthOnMobile,
    disabled = false,
    loading = false,
    type = "button",
    test,
    tabIndex,
    dataAttributes,
    block,
    "aria-label": ariaLabel,
    ...buttonHtmlAttributes
}: ButtonComponentProps) => {
    const handleClick = (e: SyntheticEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(e);
        }
    };

    const btnOutlineColor = `btn${outline ? "-outline" : ""}-${
        color === "transparent" ? "gray" : color
    }`;

    return (
        <button
            {...buttonHtmlAttributes}
            aria-label={ariaLabel ?? undefined}
            data-test={test}
            type={type}
            form={form}
            className={classnames(
                "btn",
                className,
                fullWidth && "w-100",
                fullWidthOnMobile && "button-component--full-width-mobile",
                loading && "btn--loading",
                color === "transparent" && "button-component--transparent",
                block ? "btn-block" : false,
                btnOutlineColor,
                size ? `btn-${size}` : "",
            )}
            onClick={e => {
                if (stopPropagation) {
                    e.stopPropagation();
                }
                handleClick(e);
            }}
            disabled={disabled || loading}
            ref={buttonRef}
            tabIndex={tabIndex}
            {...dataAttributes}
        >
            {children}
        </button>
    );
};

export { Button };
